/**
 * PrintLeadMagnet — dedikált, chrome-mentes nyomtatási route a
 * MIKROMÁGNES (Ügyfélmágnes) szerveroldali Playwright PDF exportjához.
 *
 * URL séma:
 *   • `/print/lead-magnet?draft=<id>` — friss, még el nem mentett Mikromágnes
 *     (backend `lead_magnet_pdf_drafts` collection-ből tölt)
 *   • `/print/lead-magnet?id=<entry_id>` — Tudástárból mentett bejegyzés
 *
 * KÜLÖNBSÉG A BLUEPRINT-EXPORTHOZ KÉPEST
 *   • Borító: Vonzás-ciánkék (#75dff5), CSAK a riport címe (a `## H2` cím a
 *     body-ból). Semmi alcím, semmi eyebrow.
 *   • "Készítette:" = a coach SAJÁT neve, Vezetéknév Keresztnév sorrendben
 *     (a Google profil általában „Keresztnév Vezetéknév" formában adja, ezért
 *     megfordítjuk). NINCS MEX Magic sehol.
 *   • NINCS impressum, NINCS MEX láblec — a backend Playwright csak
 *     diszkrét oldalszámot rak alulra.
 *
 * UGYANAZOK A PRINT-SZABÁLYOK
 *   • @page margin: 0 a CSS-ből → krém éltől élig
 *   • print-color-adjust: exact → kék akcentok és kártya-keretek átjönnek
 *   • break-inside: avoid a callout-okon / kártyákon / num-step-eken
 *   • A felső krém-levegőt a tartalom WRAPPER-jének padding-top-ja adja
 *     (a kerettel rendelkező blokkok pdf-block-wrap-be csomagolva)
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const ATTRACTION_CYAN = "#75dff5";  // Vonzás layer accent
const CREAM_PAGE_BG  = "#f4f1ea";

/** Magyar névsorrend: a Google `name` általában „Keresztnév Vezetéknév";
 * 2 részből álló nevet megfordítjuk Vezetéknév Keresztnév sorrendre. */
function toHungarianName(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
  return trimmed;
}

/** A body-ból vezető `## H2` sort kihámozzuk - ez a Mikromágnes címe. */
function splitTitleFromBody(raw) {
  const text = String(raw || "");
  const lines = text.split("\n");
  let title = "";
  let start = 0;
  while (start < lines.length && lines[start].trim() === "") start++;
  if (start < lines.length && lines[start].startsWith("## ")) {
    title = lines[start].replace(/^##\s+/, "").trim();
    start++;
    while (start < lines.length && lines[start].trim() === "") start++;
  }
  let body = lines.slice(start).join("\n");
  body = body.replace(/^####\s+/gm, "").replace(/^##\s+/gm, "");
  return { title: title.trim(), body: body.replace(/^\n+/, "").replace(/\n+$/, "") };
}

/* ════════════════════════════════════════════════════════════════════
 * INLINE MARKDOWN RENDERER
 * A LeadMagnetMicro `_MikromagnesMarkdown`-jával vizuálisan IDENTIKUS —
 * ugyanazok a font-méretek, színek, kék elválasztó, callout-szabályok,
 * num-step kártya. A user kérése: "Ne változtass a riport tartalmi
 * dizájnján" — ez a renderer pont az.
 * ════════════════════════════════════════════════════════════════════ */
const _CALLOUT_RULES = [
  { tone: "positive", patterns: [
    /^ezt vidd magaddal/i, /^mit vihetsz tovább/i, /^ha most csak egy dolgot teszel/i,
    /^ha csak egy dolgot javít/i, /^mi legyen a következő apró lépés/i,
    /^mi változhat ettől/i, /^mire figyelj a végén/i,
    /^ha szeretnéd ezt továbbvinni/i, /^ha kíváncsi vagy a folytatásra/i,
  ]},
  { tone: "warning", patterns: [
    /^ezt ne írd/i, /^ezt most ne csináld/i, /^mit ne csinálj/i,
  ]},
  { tone: "calm", patterns: [
    /^ha most ebben vagy/i,
  ]},
];
function _detectCalloutTone(heading) {
  const h = (heading || "").trim();
  for (const r of _CALLOUT_RULES) if (r.patterns.some((rx) => rx.test(h))) return r.tone;
  return null;
}
function _renderInline(text, key) {
  if (!text) return null;
  const parts = [];
  let rest = text.replace(/\*\*\*\*/g, "**");
  let i = 0;
  while (rest.length > 0) {
    const m = rest.match(/\*\*([^*]+)\*\*/);
    if (!m) { parts.push(rest); break; }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    parts.push(<strong key={`b-${key}-${i++}`} style={{ fontWeight: 700, color: "#0a0a12" }}>{m[1]}</strong>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}
function _MdBullet({ children }) {
  return (
    <li style={{
      position: "relative", paddingLeft: 22, marginBottom: 6,
      fontSize: 15, color: "#1a1a22", lineHeight: 1.65,
    }}>
      <span style={{
        position: "absolute", left: 4, top: 9,
        width: 6, height: 6, borderRadius: 3, background: "#26A9C9",
      }} />
      {children}
    </li>
  );
}
function _MdNumStep({ n, children }) {
  return (
    <div className="pdf-block-wrap lm-numstep-block" style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      padding: "12px 14px", marginBottom: 0,
      background: "#ffffff",
      border: "1px solid rgba(38,169,201,0.22)",
      borderRadius: 10,
      boxShadow: "0 1px 2px rgba(10,10,18,0.03)",
    }}>
      <div style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
        background: "#26A9C9",
        color: "#ffffff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700,
        boxShadow: "0 1px 2px rgba(38,169,201,0.40)",
      }}>{n}</div>
      <div style={{ flex: 1, fontSize: 15, color: "#1a1a22", lineHeight: 1.65 }}>
        {children}
      </div>
    </div>
  );
}
function _MdCallout({ tone, title, children }) {
  const palette = {
    positive: { bg: "#ECF7FA", border: "rgba(38,169,201,0.35)", label: "#135b6c", icon: "✓" },
    warning:  { bg: "#FFF5EC", border: "rgba(196,118,71,0.32)", label: "#7a3f1c", icon: "!" },
    calm:     { bg: "#F4F1FB", border: "rgba(124,108,242,0.28)", label: "#3f3590", icon: "·" },
  }[tone] || { bg: "#F7F4ED", border: "rgba(10,10,18,0.10)", label: "#333", icon: "·" };
  return (
    <div className="pdf-block-wrap lm-callout-block" style={{
      background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 12, padding: "16px 18px", marginTop: 0, marginBottom: 0,
    }}>
      {title ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 10.5, color: palette.label, fontWeight: 700,
          letterSpacing: "0.14em", textTransform: "uppercase",
          marginBottom: 10,
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 16, height: 16, borderRadius: 999,
            background: palette.border, color: "#fff", fontSize: 10, fontWeight: 700,
          }}>{palette.icon}</span>
          {title}
        </div>
      ) : null}
      <div style={{ fontSize: 15, color: "#1a1a22", lineHeight: 1.65 }}>
        {children}
      </div>
    </div>
  );
}
function _parseBlockChildren(lines, keyPrefix) {
  // KIMENET: top-level `.pdf-block-wrap` div-ek tömbje. Minden flush a saját
  // wrapperébe kerül - paragrafus, bullet-lista, num-step lista, és a
  // bekezdés-szakasz blokkok mind külön break-boundary-vé válnak. A felső
  // krém-levegőt a CSS-ben definiált `padding-top` adja minden blokkra,
  // a blokk keretén KÍVÜL - így folytatóoldalon sem tapad a lap tetejéhez.
  const out = [];
  let para = [];
  let bullets = [];
  let nums = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(" ").trim();
    para = [];
    if (!text) return;
    const k = `${keyPrefix}-p-${out.length}`;
    out.push(
      <div key={k} className="pdf-block-wrap lm-text-block">
        <p style={{ fontSize: 15, color: "#1a1a22", lineHeight: 1.65, margin: 0 }}>
          {_renderInline(text, k)}
        </p>
      </div>
    );
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    const k = `${keyPrefix}-ul-${out.length}`;
    out.push(
      <div key={k} className="pdf-block-wrap lm-list-block">
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {bullets.map((b, i) => (
            <_MdBullet key={`${k}-li-${i}`}>{_renderInline(b, `${k}-li-${i}`)}</_MdBullet>
          ))}
        </ul>
      </div>
    );
    bullets = [];
  };
  const flushNums = () => {
    if (!nums.length) return;
    // A num-step kártyák EGYENKÉNT top-level break-boundary-k (külön
    // pdf-block-wrap-k), hogy bármelyikük szakadhasson új oldal tetejére
    // a saját padding-top-jával.
    nums.forEach((entry, i) => {
      const k = `${keyPrefix}-ns-${out.length}`;
      out.push(
        <_MdNumStep key={`${k}-${i}`} n={entry.n}>
          {_renderInline(entry.text, `${k}-${i}`)}
        </_MdNumStep>
      );
    });
    nums = [];
  };
  const flushAll = () => { flushPara(); flushBullets(); flushNums(); };

  for (const raw of lines) {
    const ln = raw.replace(/\t/g, "  ");
    if (/^\s*-{3,}\s*$/.test(ln)) { flushAll(); continue; }
    if (/^\s*$/.test(ln)) { flushAll(); continue; }
    const numMatch = ln.match(/^\s*(\d+)\.\s+(.*)$/);
    if (numMatch) {
      flushPara(); flushBullets();
      nums.push({ n: parseInt(numMatch[1], 10), text: numMatch[2].trim() });
      continue;
    }
    const bulMatch = ln.match(/^\s*[-*]\s+(.*)$/);
    if (bulMatch) { flushPara(); flushNums(); bullets.push(bulMatch[1].trim()); continue; }
    flushBullets(); flushNums();
    para.push(ln.trim());
  }
  flushAll();
  return out;
}
function MikromagnesMarkdownPrint({ source }) {
  const text = (source || "").trim();
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const sections = [];
  let cursor = { heading: null, lines: [] };
  for (const ln of lines) {
    const m = ln.match(/^###\s+(.+?)\s*$/);
    if (m) {
      if (cursor.heading != null || cursor.lines.length > 0) sections.push(cursor);
      cursor = { heading: m[1].trim().replace(/^[#*\s]+/, ""), lines: [] };
    } else if (ln.match(/^##\s+/)) {
      continue;
    } else {
      cursor.lines.push(ln);
    }
  }
  if (cursor.heading != null || cursor.lines.length > 0) sections.push(cursor);

  // LAPOS struktúra: minden szekciónak a CÍMÉ + a benne lévő blokkjai
  // mind külön top-level `.pdf-block-wrap` testvérek. Nincs külső
  // `pdf-lm-section` atomic wrapper - ezzel megengedjük, hogy az
  // oldaltörés a blokkok KÖZÉ essen, és minden blokk saját padding-top-ja
  // adja a felső krém-levegőt a folytatóoldalon.
  const blocks = [];
  sections.forEach((sec, sIdx) => {
    const tone = _detectCalloutTone(sec.heading);
    const items = _parseBlockChildren(sec.lines, `s${sIdx}`);
    if (tone) {
      // Callout-szekció: a cím a callout-on belül, a callout maga top-level
      // pdf-block-wrap (a _MdCallout-ban).
      blocks.push(
        <_MdCallout key={`c${sIdx}`} tone={tone} title={sec.heading}>
          {items}
        </_MdCallout>
      );
    } else {
      // Sima szekció: a cím külön `lm-heading-block` blokk, utána a
      // tartalom blokkok testvérként követik. A heading blokk kap egy
      // `keep-with-next`-szerű viselkedést a CSS-ből (`break-after: avoid`),
      // hogy ne maradjon árván egy oldal alján.
      if (sec.heading) {
        blocks.push(
          <div key={`h${sIdx}`} className="pdf-block-wrap lm-heading-block" data-section-index={sIdx}>
            <h3 style={{
              fontFamily: "inherit",
              fontSize: 15, fontWeight: 600, color: "#26A9C9",
              letterSpacing: "-0.005em", lineHeight: 1.35,
              margin: 0,
            }}>
              {sec.heading}
            </h3>
            <div style={{
              marginTop: 8, height: 1, width: 60,
              background: "linear-gradient(90deg, rgba(38,169,201,0.32), rgba(38,169,201,0))",
            }} />
          </div>
        );
      }
      items.forEach((it) => blocks.push(it));
    }
  });

  return (
    <>
      {blocks}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * COVER OLDAL
 * Vonzás-ciánkék akcent. CSAK a Mikromágnes címe + diszkrét coach név.
 * NINCS eyebrow, NINCS alcím, NINCS MEX Magic.
 * ════════════════════════════════════════════════════════════════════ */
function CoverPage({ title, coachName }) {
  return (
    <section className="pdf-page pdf-page--cover pdf-page--lm-cover" data-print-page="cover">
      <div className="pdf-cover-inner">
        {/* Felső accent szalag */}
        <div
          style={{
            height: 4,
            width: 56,
            background: ATTRACTION_CYAN,
            borderRadius: 2,
            marginBottom: 60,
          }}
        />

        {/* Riport-cím */}
        <h1
          style={{
            fontSize: 52,
            fontWeight: 600,
            color: "#15141c",
            letterSpacing: "-0.022em",
            lineHeight: 1.06,
            margin: 0,
            marginBottom: 96,
            maxWidth: "94%",
          }}
        >
          {title || "Mikromágnes"}
        </h1>

        {/* "Készítette:" sor — coach saját neve, magyar sorrendben.
            Kis méret, diszkrét. SEMMI MEX Magic. */}
        <div style={{ marginTop: "auto" }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.20em",
              textTransform: "uppercase",
              color: "#7d7a8c",
              marginBottom: 8,
            }}
          >
            Készítette
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "#3a384a",
              letterSpacing: "-0.005em",
              lineHeight: 1.3,
            }}
          >
            {coachName || ""}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ════════════════════════════════════════════════════════════════════ */
export default function PrintLeadMagnet() {
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draft") || "";
  const entryId = searchParams.get("id") || "";

  const [content, setContent] = useState(null);
  const [coachName, setCoachName] = useState("");
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState(null);

  // ── Body/HTML krém — a print.css `print-route` szelektorai aktívak. ──
  useEffect(() => {
    if (typeof window !== "undefined") window.__PRINT_READY__ = false;
    const prev = {
      bodyBg: document.body.style.background,
      htmlBg: document.documentElement.style.background,
      bodyColor: document.body.style.color,
    };
    document.body.classList.add("print-route");
    document.body.style.background = CREAM_PAGE_BG;
    document.documentElement.style.background = CREAM_PAGE_BG;
    document.body.style.color = "#15141c";
    return () => {
      document.body.classList.remove("print-route");
      document.body.style.background = prev.bodyBg;
      document.documentElement.style.background = prev.htmlBg;
      document.body.style.color = prev.bodyColor;
    };
  }, []);

  // ── Coach név + tartalom betöltése ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Coach név (Google profil → magyar sorrend)
      try {
        const me = await api.get("/auth/me");
        if (!cancelled) setCoachName(toHungarianName(me.data?.name || ""));
      } catch {
        if (!cancelled) setCoachName("");
      }
      // Tartalom forrás: draft VAGY saved entry
      try {
        let title = "";
        let body  = "";
        if (draftId) {
          const r = await api.get(`/lead-magnet/pdf-draft/${draftId}`);
          title = (r.data?.title || "").trim();
          body  = (r.data?.mikromagnes_body || "").trim();
        } else if (entryId) {
          const r = await api.get(`/asset-vault/${entryId}`);
          const bp = r.data?.blueprint || {};
          body  = (bp.mikromagnes_body || "").trim();
          title = (r.data?.name || "").trim();
        } else {
          throw new Error("Hiányzó draft vagy entry ID");
        }
        // Title fallback: a body vezető `## H2` sora
        const fromBody = splitTitleFromBody(body).title;
        const finalTitle = title || fromBody || "Mikromágnes";
        if (!cancelled) {
          setContent({ title: finalTitle, body });
          setResolved(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Betöltési hiba");
          setResolved(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [draftId, entryId]);

  // ── Render-ready jel ─────────────────────────────────────────────
  useEffect(() => {
    if (!resolved || !content) return;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const fire = () => {
          window.__PRINT_READY__ = true;
          window.dispatchEvent(new Event("blueprint-print-ready"));
        };
        if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function") {
          document.fonts.ready.then(fire);
        } else {
          fire();
        }
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [resolved, content]);

  if (!resolved) {
    return (
      <div className="print-page-root" data-testid="print-loading" style={{ padding: 40, fontFamily: "Inter, sans-serif" }}>
        Betöltés…
      </div>
    );
  }
  if (error) {
    return (
      <div className="print-page-root" data-testid="print-error" style={{ padding: 40, fontFamily: "Inter, sans-serif", color: "#a55" }}>
        Hiba: {error}
      </div>
    );
  }

  return (
    <div className="print-page-root">
      <CoverPage title={content.title} coachName={coachName} />

      <section className="pdf-page pdf-page--report pdf-page--lm-report" data-print-page="report">
        <div data-testid="lead-magnet-output">
          <MikromagnesMarkdownPrint source={splitTitleFromBody(content.body).body || content.body} />
        </div>
      </section>
    </div>
  );
}
